import React, { useState, useMemo } from 'react';
import { Tooltip, LineChart, ResponsiveContainer, Line, XAxis, ReferenceLine } from 'recharts';

import {
  Grid,
  Typography,
  ClickAwayListener,
  IconButton,
  Link,
  Button,
  Backdrop,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_YELLOW,
  COLOR_RED_LIGHT,
  COLOR_E,
  COLOR_ACCENT_2_CONTRAST,
  LIGHT_GREY,
  percentageNumberNameMap,
  timePreiodNumberNameMap,
} from 'src/const';
import { convertCurrency, formatCurrency, makeDownloadableBlob } from 'src/utils/functions';
import { Revenue, ADR, OccupancyRate } from 'src/interfaces/idashboard';

const useStyles = makeStyles((theme: Theme) => ({
  cardRoot: {
    height: 280,
    [theme.breakpoints.down(1040)]: {
      height: 320,
    },
    [theme.breakpoints.down(900)]: {
      height: 260,
    },
    padding: '16px',
  },
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
    transform: 'rotate(-30deg)',
    msTransform: 'rotate(-30deg)' /* IE 9 */,
    webkitTransform: 'rotate(-30deg),' /* Opera, Chrome, and Safari */,
  },
}));

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

const CustomTooltip = ({ active, payload, label, classes, title }) => {
  if (active && payload && payload.length > 0) {
    const _payload = payload[0].payload;
    return (
      <Card>
        <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
          <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
            {payload[0].payload.timePeriod}
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {title} {_payload[title].toFixed(2)}
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return null;
};

const CustomizedDot = props => {
  //linechart
  const { cx, cy, value, color, getHighLowPoints } = props;

  const limit = getHighLowPoints();

  const size = 6;
  const smallSize = 4;

  if (value === limit.high || value === limit.low) {
    return (
      <svg x={cx - size} y={cy - size} width={size * 2} height={size * 2} fill="white">
        <g transform={`translate(${size} ${size})`}>
          <circle r={size} fill={color} />
          {/* <circle r={size / 2} fill="white" /> */}
        </g>
      </svg>
    );
  } else if (value !== null && value >= 0) {
    return (
      <svg
        x={cx - smallSize}
        y={cy - smallSize}
        width={smallSize * 2}
        height={smallSize * 2}
        fill="white"
      >
        <g transform={`translate(${smallSize} ${smallSize})`}>
          <circle r={smallSize} fill={color} />
          <circle r={smallSize / 2} fill="white" />
        </g>
      </svg>
    );
  } else {
    return (
      <svg
        x={cx - smallSize}
        y={cy - smallSize}
        width={smallSize * 2}
        height={smallSize * 2}
        fill="white"
      >
        <g transform={`translate(${smallSize} ${smallSize})`}>
          <circle r={smallSize} fill={'#0000'} />
        </g>
      </svg>
    );
  }
};

const GraphViewKeyMetrics = ({
  title,
  keyMetricsVal,
  aggregateData,
  color,
  currency,
  unit,
  metricsKey,
  percentile,
  timePeriod,
  setTimePeriod,
}) => {
  const classes = useStyles();

  const yAxisName = title.includes('Revenue') ? 'Revenue' : title;

  const getHighLowPoints = () => {
    let low = Infinity;
    let high = -Infinity;
    keyMetricsVal.forEach(data => {
      if (data[`${metricsKey}`] != 'missing' && data[`${metricsKey}`] != 'inactive') {
        if (high < data[`${metricsKey}`]) {
          high = data[`${metricsKey}`];
        }
        if (low > data[`${metricsKey}`]) {
          low = data[`${metricsKey}`];
        }
      }
    });
    return { high, low };
  };

  const handleTimePeriodChange = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    setTimePeriod(value);
  };

  const makeKeyMatricesObjectToArray = (data: ADR[] | OccupancyRate[] | Revenue[]) => {
    const keys: string[] = [];
    for (const key in data[0]) {
      keys.push(key);
    }
    const dateKey = keys[0];
    const dataKey = keys[1];

    const resultArray: [string, string][] = [];

    resultArray.push(['Dates', title + ` (${unit})`]);

    for (const row of data) {
      if (!title.includes('occupancy_rate')) {
        resultArray.push([row[dateKey], '' + convertCurrency(currency, Number(row[dataKey]))]);
      } else {
        resultArray.push([row[dateKey], row[dataKey]]);
      }
    }

    return resultArray;
  };

  const getKeyMetricsCSVDataAsArray = () => {
    if (!keyMetricsVal) return null;

    const keyMatricesCSVDataAsArray = makeKeyMatricesObjectToArray(keyMetricsVal);
    makeDownloadableBlob(
      keyMatricesCSVDataAsArray,
      `${title}-${timePreiodNumberNameMap[timePeriod]}-${percentageNumberNameMap[percentile]}`,
      false
    );

    return null;
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      item
      xs={12}
      className={classes.cardRoot}
    >
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h2" style={cardTitle}>
          {title}
          <span style={{ fontWeight: 'bold', margin: '0px 6px' }}>
            {Number(aggregateData).toFixed(2)}
          </span>
          <IconButton style={infoIconButton}>
            <CloudDownloadOutlinedIcon
              style={{
                ...infoIconStyle,
                fontSize: 24,
                color: COLOR_ACCENT_2_CONTRAST,
              }}
              onClick={() => getKeyMetricsCSVDataAsArray()}
            />
          </IconButton>
        </Typography>
      </Grid>

      <Grid container direction="row" justifyContent="flex-start" spacing={0}>
        <Grid
          container
          item
          xs={12}
          direction="row"
          style={{ position: 'relative', top: '0', left: '0' }}
        >
          <Grid item xs={12}>
            <ResponsiveContainer height={100} width="100%">
              <LineChart data={keyMetricsVal} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <Line
                  animationDuration={150}
                  isAnimationActive={false}
                  type="monotone"
                  dataKey={yAxisName}
                  stroke={color}
                  strokeWidth={3}
                  unit={unit}
                  dot={<CustomizedDot color={color} getHighLowPoints={getHighLowPoints} />}
                />
                <XAxis dataKey="timePeriod" hide={true} />
                <ReferenceLine
                  x={
                    keyMetricsVal.length > 0
                      ? getStartingEndingDate(keyMetricsVal[0].date, true)
                      : ''
                  }
                  stroke={color}
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeDasharray="4 12"
                />
                <ReferenceLine
                  x={
                    keyMetricsVal.length > 0
                      ? getStartingEndingDate(keyMetricsVal[keyMetricsVal.length - 1].date, true)
                      : ''
                  }
                  stroke={color}
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeDasharray="4 12"
                />
                <Tooltip
                  content={<CustomTooltip classes={classes} active payload label title={title} />}
                  wrapperStyle={{ zIndex: 100000 }}
                  filterNull={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{
              fontFamily: 'roboto',
              fontSize: '10px',
              color: '#555',
            }}
          >
            <>
              <Grid item>
                {keyMetricsVal.length > 0 && getStartingEndingDate(keyMetricsVal[0].date)}
              </Grid>
              <Grid item>
                {keyMetricsVal.length > 0 &&
                  getStartingEndingDate(keyMetricsVal[keyMetricsVal.length - 1].date)}
              </Grid>
            </>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ marginTop: '20px' }}
      >
        <FormControl variant="standard" size="small" sx={{ width: 150 }}>
          <InputLabel id="time-period-select">Time Period</InputLabel>
          <Select
            labelId="time-period-select"
            id="time-period-select"
            value={`${timePeriod}`}
            onChange={handleTimePeriodChange}
            label="Time Period"
          >
            <MenuItem value={6}>Last 6 months</MenuItem>
            <MenuItem value={12}>Last 12 months</MenuItem>
            <MenuItem value={100}>All history</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

const getSeasonalityBarColor = (seaosnality: number) => {
  if (seaosnality <= 50) return COLOR_GREEN;
  else if (seaosnality <= 75) return COLOR_YELLOW;
  else return COLOR_RED_LIGHT;
};

const getStartingEndingDate = (date: any, full: boolean = false): string => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  if (!date) return '';

  const yearMonthArray = date.split('-');
  const year = yearMonthArray[0];
  const fullMonth = monthNames[~~yearMonthArray[1] - 1]; //~~ -> converts into an integer
  const shortMonth = fullMonth.substring(0, 3);
  return full ? fullMonth + ', ' + year : shortMonth + ', ' + year;
};

export default GraphViewKeyMetrics;
